import React from 'react';
import "./smile-pages.css";

export default function CanSmilingHelpYouLiveLonger() {
  return (
    <div className="container-box mt-5 mb-5 smile-page">

      <h1>Can Smiling Help You Live Longer?</h1>

      <p>
        Smiling may help you live longer by reducing stress, improving heart health, strengthening
        social bonds, and promoting a more optimistic outlook — all of which are linked to 
        <strong> greater longevity</strong> in scientific studies. In short, smiling isn’t just a 
        sign of happiness — it’s a <strong>tool for long-term health and survival.</strong>
      </p>

      <p>Here’s how smiling contributes to a longer life, backed by science:</p>

      <hr />

      {/* 1 */}
      <h3>🧠 1. Smiling Reduces Stress — and Stress Shortens Lifespan</h3>

      <p>
        Chronic stress raises cortisol levels, damages the heart, weakens the immune system, and 
        accelerates aging. Smiling can reverse or reduce these effects.
      </p>

      <p><strong>How:</strong></p>
      <ul>
        <li>Smiling activates the <strong>parasympathetic nervous system</strong> (your body’s calming system)</li>
        <li>Lowers <strong>heart rate</strong>, <strong>blood pressure</strong>, and <strong>cortisol levels</strong></li>
        <li>Promotes <strong>relaxation</strong>, which protects your brain and body from stress-related damage</li>
      </ul>

      <p><strong>📚 Study:</strong><br />
      Kraft & Pressman (2012) — People who smiled during stress-inducing tasks had lower heart rates and recovered more quickly than those who didn’t smile.
      </p>

      <hr />

      {/* 2 */}
      <h3>❤️ 2. Smiling Improves Cardiovascular Health</h3>

      <p>
        Smiling and laughter increase <strong>blood flow</strong> and improve <strong>vascular function</strong>, 
        reducing your risk of heart disease — the leading cause of death worldwide.
      </p>

      <p><strong>How:</strong></p>
      <ul>
        <li>Stimulates <strong>oxygen intake</strong></li>
        <li><strong>Enhances</strong> blood vessel function</li>
        <li>Reduces <strong>inflammatory stress responses</strong> in the cardiovascular system</li>
      </ul>

      <p><strong>📚 Study:</strong><br />
      University of Maryland (2005) found that laughter (which usually includes smiling) improves 
      <strong> blood vessel dilation</strong>, similar to the effects of aerobic exercise.
      </p>

      <hr />

      {/* 3 */}
      <h3>👯 3. Smiling Strengthens Social Bonds — a Major Longevity Factor</h3>

      <p>People with strong social connections tend to live longer. Smiling:</p>

      <ul>
        <li>Increases <strong>likability</strong> and <strong>approachability</strong></li>
        <li>Builds and maintains <strong>social networks</strong></li>
        <li>Enhances <strong>emotional support</strong>, which boosts physical and mental resilience</li>
      </ul>

      <p><strong>📚 Study:</strong><br />
      Harvard’s 80-year <strong>Grant Study</strong> found that relationships are one of the strongest 
      predictors of long-term health and longevity.
      </p>

      <p>Smiling keeps those relationships thriving.</p>

      <hr />

      {/* 4 */}
      <h3>🔍 4. Smiling Reflects a Positive, Optimistic Outlook — Which Is Linked to Living Longer</h3>

      <p>
        Smiling is often a <strong>natural expression of optimism</strong>, and studies show that optimistic 
        people are:
      </p>

      <ul>
        <li><strong>More resilient</strong> under pressure</li>
        <li><strong>Less prone</strong> to disease</li>
        <li>More likely to engage in <strong>healthy behaviors</strong></li>
      </ul>

      <p><strong>📚 Study:</strong><br />
      A 2019 study published in <strong>PNAS</strong> (Lee et al.) found that people with a more optimistic 
      outlook had an <strong>11–15% longer lifespan</strong> and were more likely to reach age 85 or older.
      </p>

      <hr />

      {/* 5 */}
      <h3>🏈 5. Smiling May Predict Lifespan — Literally</h3>

      <p>One of the most fascinating studies looked at professional athletes' smiles.</p>

      <p><strong>📚 Study: Ernest L. Abel & Michael L. Kruger (2010)</strong><br />
      Researchers analyzed the smiles of Major League Baseball players from their 1952 baseball cards. 
      The results were stunning:
      </p>

      <ul>
        <li>Players with <strong>no smile</strong> lived an average of <strong>72.9 years</strong></li>
        <li>Those with <strong>partial smiles</strong> lived <strong>75.0 years</strong></li>
        <li>Players with <strong>genuine, Duchenne smiles</strong> lived <strong>79.9 years</strong></li>
      </ul>

      <p><strong>👉 Source:</strong> Psychological Science</p>

      <hr />

      {/* Summary Table */}
      <h3>✅ Summary: How Smiling Helps You Live Longer</h3>

      <table className="summary-table">
        <thead>
          <tr>
            <th>Smile Benefit</th>
            <th>Longevity Impact</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Reduces stress hormones</td>
            <td>Protects brain, heart, and immune system</td>
          </tr>
          <tr>
            <td>Improves cardiovascular function</td>
            <td>Lowers heart disease risk</td>
          </tr>
          <tr>
            <td>Strengthens social relationships</td>
            <td>Increases emotional and physical support</td>
          </tr>
          <tr>
            <td>Reflects and reinforces optimism</td>
            <td>Boosts resilience and healthy habits</td>
          </tr>
          <tr>
            <td>Predicts positive emotional health</td>
            <td>Correlates with significantly longer lifespan</td>
          </tr>
        </tbody>
      </table>

      <hr />

      <h3>🔑 Keys to Remember</h3>

      <p>
        <strong>Smiling is a longevity superpower.</strong> Smiling calms your nervous system, protects your heart, 
        deepens your social bonds, and helps you bounce back from life’s challenges — all key ingredients in 
        living a longer, healthier life.
      </p>
      <p>
         <strong>That Smile App is here to help you live a longer, healthier life.</strong>
      </p>

    </div>
  );
}
